import React, { useMemo, useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Card, CardHeader, CardContent, CardFooter } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Progress } from "@/components/ui/progress";
import { Check, Loader2 } from "lucide-react";
import { authorizedFetch } from '@/lib/queryClient';
import { toast } from '@/hooks/use-toast';
import { inc as metricsInc } from '@/lib/metrics';
import { useAuth } from '@/auth/AuthProvider';
import { isUnlimitedCredits } from '@/hooks/useCredits';

export default function BillingModal({ open, onOpenChange }: { open: boolean; onOpenChange: (v: boolean) => void; }) {
	const { user } = useAuth();
	const [credits, setCredits] = useState<any>(null);
	const [config, setConfig] = useState<any>(null);
	const [billing, setBilling] = useState<any>(null);
	const [loading, setLoading] = useState<boolean>(false);
	const [hydrating, setHydrating] = useState<boolean>(false);
	const [annual, setAnnual] = useState<boolean>(false);
	const [manageOpen, setManageOpen] = useState<boolean>(false);

	useEffect(() => {
		if (!open) return;
		
		console.log('[BillingModal] useEffect triggered, open:', open, 'user:', user?.id || 'null');
		
		let cancelled = false;
		const load = async () => {
			setHydrating(true);
			console.log('[BillingModal] Starting to load billing data...');
			try {
				let c: any = null;
				let cfg: any = {};
				
				// Load config first (doesn't require auth) - use Promise.all for parallel loading
				const loadConfig = async () => {
					try {
						const rcfg = await authorizedFetch('/api/config');
						if (cancelled) return null;
						if (rcfg.ok) return await rcfg.json();
						else console.warn('[BillingModal] Config fetch failed:', rcfg.status);
					} catch (e) {
						if (cancelled) return null;
						console.error('[BillingModal] Config fetch error:', e);
					}
					return null;
				};
				
				// Load credits if user is authenticated
				const loadCredits = async () => {
					if (!user) {
						console.warn('[BillingModal] No user object, skipping credits fetch');
						return null;
					}
					console.log('[BillingModal] loadCredits: Starting fetch for user:', user.id);
					try {
						// Debug: Check if we have a token
						const { getAccessToken } = await import('@/lib/queryClient');
						const token = await getAccessToken();
						console.log('[BillingModal] loadCredits: Token available:', !!token, token ? token.substring(0, 20) + '...' : 'null');
						if (!token) {
							console.warn('[BillingModal] No access token available, user might not be authenticated');
							return null;
						}
						
						const creditsUrl = '/api/credits?' + new URLSearchParams({ _t: String(Date.now()) });
						console.log('[BillingModal] loadCredits: Fetching from:', creditsUrl);
						
						// Add timeout to prevent infinite hangs
						const timeoutPromise = new Promise<Response>((_, reject) => {
							setTimeout(() => reject(new Error('Request timeout after 10 seconds')), 10000);
						});
						
						const fetchPromise = authorizedFetch(creditsUrl);
						const rc = await Promise.race([fetchPromise, timeoutPromise]);
						console.log('[BillingModal] loadCredits: Response received, status:', rc.status, 'cancelled:', cancelled);
						if (cancelled) {
							console.warn('[BillingModal] loadCredits: Request cancelled');
							return null;
						}
						if (rc.ok) {
							const data = await rc.json();
							console.log('[BillingModal] loadCredits: Success, data:', data);
							return data;
						} else {
							const errorText = await rc.text().catch(() => '');
							console.warn('[BillingModal] Credits fetch failed:', rc.status, errorText);
							if (rc.status === 401) {
								console.warn('[BillingModal] Authentication failed - token might be missing or invalid');
							}
						}
					} catch (e) {
						if (cancelled) {
							console.warn('[BillingModal] loadCredits: Exception but request was cancelled');
							return null;
						}
						console.error('[BillingModal] Credits fetch error:', e);
						if ((e as any)?.message?.includes('401') || (e as any)?.message?.includes('missing_auth_token')) {
							console.error('[BillingModal] Authentication error - check if user session is valid');
						}
					}
					return null;
				};
				
				// Load billing summary if user is authenticated
				const loadBilling = async () => {
					if (!user) return;
					try {
						const billingUrl = '/api/billing/summary?' + new URLSearchParams({ _t: String(Date.now()) });
						const bs = await authorizedFetch(billingUrl);
						if (cancelled) return;
						if (bs.ok) setBilling(await bs.json());
						else console.warn('[BillingModal] Billing summary fetch failed:', bs.status, await bs.text().catch(() => ''));
					} catch (e) {
						if (cancelled) return;
						console.error('[BillingModal] Billing summary fetch error:', e);
					}
				};
				
				// Load config and credits in parallel, billing summary separately
				const [configResult, creditsResult] = await Promise.all([
					loadConfig(),
					loadCredits()
				]);
				
				if (cancelled) return;
				
				cfg = configResult || {};
				c = creditsResult;
				
				// Fallbacks if unauth or requests failed
				if (!c) c = { plan: 'free', allowance: 0, remaining: 0, resetAt: null, enabled: false };
				
				setCredits(c);
				setConfig(cfg);
				
				// Load billing summary (non-blocking)
				loadBilling();
				
			} catch (e) {
				if (cancelled) return;
				console.error('[BillingModal] Failed to load billing data', e);
				// Set fallback values even on error
				setCredits({ plan: 'free', allowance: 0, remaining: 0, resetAt: null, enabled: false });
				setConfig({});
			} finally { 
				if (!cancelled) setHydrating(false); 
			}
		};
		load();
		return () => { cancelled = true; };
	}, [open, user]);

	// Initialize the annual toggle based on user's current subscription interval when billing summary loads
	useEffect(() => {
		try {
			const iv = String(billing?.subscription?.interval || '').toLowerCase();
			if (iv === 'month') setAnnual(false);
			if (iv === 'year') setAnnual(true);
		} catch {}
	}, [billing?.subscription?.interval]);

	useEffect(() => {
		async function maybeRecover() {
			if (!user) return;
			const isFree = (credits?.plan || 'free') === 'free';
			const cpe = billing?.subscription?.current_period_end || billing?.subscription?.paid_through_end || null;
			if (isFree && cpe) {
				try {
					const sync = await authorizedFetch('/api/billing/sync', { method: 'POST', headers: { 'Content-Type': 'application/json' } });
					if (sync.ok) {
						// Re-fetch credits after sync
						try {
							const rc = await authorizedFetch('/api/credits');
							if (rc.ok) setCredits(await rc.json());
						} catch {}
					}
				} catch {}
			}
		}
		maybeRecover();
	}, [open, user, credits?.plan, billing?.subscription?.current_period_end, billing?.subscription?.paid_through_end]);

	const plan = credits?.plan ?? 'free';
	const allowance = credits?.allowance ?? 0;
	const remaining = credits?.remaining ?? 0;
	const unlimited = isUnlimitedCredits(credits);
// const used = Math.max(0, allowance - remaining);
	const resetAt = credits?.resetAt ? new Date(credits.resetAt) : null;

	const priceMonthly = config?.pricing?.plusMonthlyUsd ?? 12.9;
	const priceYearly = config?.pricing?.plusYearlyUsd ?? 129;
	const priceIdMonthly = config?.stripe?.pricePlusMonthly || (config?.priceMonthly as string);
	const priceIdYearly = config?.stripe?.pricePlusYearly || (config?.priceYearly as string);
	const savings = Math.max(0, Math.round(priceMonthly * 12 - priceYearly));

	const barValue = useMemo(() => {
		if (!allowance || unlimited) return 0;
		return Math.min(100, Math.max(0, (remaining / allowance) * 100));
	}, [remaining, allowance, unlimited]);

// Removed unused fmtDate helper

// Removed unused payment method details
const cancelAtPeriodEnd = billing?.subscription?.cancel_at_period_end ?? billing?.subscription?.cancelAtPeriodEnd ?? credits?.cancelAtPeriodEnd ?? false;
// statusRaw currently unused; keep for future state mapping if needed
// NOTE: cancel_at_period_end should NOT affect plan determination - user paid until period_end
// It only affects UI messaging (showing cancellation notice)
// The backend correctly returns plan='plus' if subscription is active until period_end
const uiPlan: 'free'|'plus' = plan; // Use plan from backend, don't override based on cancelAtPeriodEnd
const userInterval = (billing?.subscription?.interval || null) as 'month' | 'year' | null;
const selectedInterval: 'month' | 'year' = annual ? 'year' : 'month';
const intervalKnown = !!userInterval;
const isPlusCurrentSelection = uiPlan === 'plus' && ((intervalKnown && userInterval === selectedInterval) || (!intervalKnown && selectedInterval === 'month'));

	async function openPortal() {
		setLoading(true);
        try {
			const r = await authorizedFetch('/api/billing/portal', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({}) });
			const { url } = await r.json();
			window.location.assign(url);
		} catch (e) {
			console.error(e);
			try { toast({ title: 'Billing unavailable', description: (e as any)?.message || 'Please try again later.' }); } catch {}
			setLoading(false);
		}
	}

	async function upgrade() {
		setLoading(true);
        try {
			const targetInterval: 'month' | 'year' = annual ? 'year' : 'month';

			// Case A: user is Plus and we know their current interval
			if (uiPlan === 'plus' && userInterval) {
				// No-op if already on target
				if (userInterval === targetInterval) {
					setLoading(false);
					return;
				}

				// Block yearly -> monthly in-app
				if (userInterval === 'year' && targetInterval === 'month') {
					toast({
						title: 'Change not supported in-app',
						description: 'To switch from yearly to monthly, please contact support and we will adjust your plan at renewal.',
					});
					setLoading(false);
					return;
				}

				// Monthly -> Yearly (allowed)
				const r = await authorizedFetch('/api/billing/upgrade-interval', {
					method: 'POST',
					headers: { 'Content-Type': 'application/json' },
					body: JSON.stringify({ targetInterval }),
				});
				const data = await r.json();

				if (!r.ok || !data.ok) {
					if (data.error === 'downgrade_not_allowed') {
						toast({
							title: 'Change not supported in-app',
							description: data.message || 'Switching from yearly to monthly is only available at renewal. Please contact support.',
						});
					} else {
						toast({
							title: 'Upgrade failed',
							description: data.message || data.error || 'Please try again later.',
						});
					}
					setLoading(false);
					return;
				}

				// Refresh billing summary & credits
				try {
					const bs = await authorizedFetch('/api/billing/summary?' + new URLSearchParams({ _t: String(Date.now()) }));
					if (bs.ok) setBilling(await bs.json());
				} catch {}
				try {
					const rc = await authorizedFetch('/api/credits?' + new URLSearchParams({ _t: String(Date.now()) }));
					if (rc.ok) setCredits(await rc.json());
				} catch {}

				try {
					metricsInc('credits_upgrade_click', { tier: targetInterval === 'year' ? 'yearly' : 'monthly', mode: 'switch_interval' });
				} catch {}

				toast({
					title: 'Plan updated',
					description: targetInterval === 'year'
						? 'You have switched to the yearly plan. Stripe applied prorated charges/refunds automatically.'
						: 'You have switched to the monthly plan.',
				});

				setLoading(false);
				return;
			}

			// Guard: Plus user without resolvable interval - prevent duplicate subscriptions
			if (uiPlan === 'plus' && !userInterval) {
				toast({
					title: 'Unable to determine subscription interval',
					description: 'Please contact support to update your subscription. We need to verify your current plan details to prevent duplicate charges.',
				});
				setLoading(false);
				return;
			}

			// Case B: user is Free - use Checkout to create first subscription
			const priceId = targetInterval === 'year' ? priceIdYearly : priceIdMonthly;
            const r = await authorizedFetch('/api/billing/checkout', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ priceId })
            });
			const { url } = await r.json();
			try { metricsInc('credits_upgrade_click', { tier: targetInterval === 'year' ? 'yearly' : 'monthly', mode: 'checkout' }); } catch {}
			window.location.assign(url);
		} catch (e) {
			console.error(e);
			try { toast({ title: 'Upgrade failed', description: (e as any)?.message || 'Please try again later.' }); } catch {}
			setLoading(false);
		}
	}

	const PlanBadge = () => (
		<div className="flex items-center gap-2">
			<div className="w-8 h-8 rounded-full bg-[#ffd500] text-neutral-900 flex items-center justify-center">
				<span className="text-xs font-semibold">{(user?.email?.charAt(0) || 'U').toUpperCase()}</span>
			</div>
			<div>
    <div className="text-sm font-semibold">
      {plan === 'plus' ? "You're on Plus" : "You're on Free"}
    </div>
			</div>
		</div>
	);

	const creditsLabel = unlimited ? 'Unlimited credits' : `${remaining} of ${allowance}`;
	const Feature = ({ children }: { children: React.ReactNode }) => (
		<div className="flex items-center gap-2 text-sm text-neutral-800"><Check className="h-4 w-4 text-green-500" />{children}</div>
	);

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent className="sm:max-w-5xl p-6 sm:p-8 max-h-[80vh] overflow-y-auto rounded-2xl border border-neutral-200 bg-white shadow-lg" aria-busy={hydrating}>
				<DialogHeader>
					<DialogTitle className="text-[22px] font-semibold text-neutral-900">Plans & Billing</DialogTitle>
					<p className="mt-1 text-sm text-neutral-600 flex items-center gap-2">
						<span>Subscription & Billing Settings</span>
						{hydrating && (
							<span className="inline-flex items-center gap-2 text-neutral-600 text-sm" role="status" aria-live="polite">
								<Loader2 className="h-4 w-4 animate-spin" />
								<span>Loading billing details…</span>
							</span>
						)}
					</p>
					<DialogDescription className="sr-only">Manage your subscription and see your credit usage.</DialogDescription>
				</DialogHeader>

				{/* Top row: current plan + credits */}
				<div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6">
					<Card className="md:col-span-1 rounded-xl border border-neutral-200 bg-white shadow-sm">
						<CardContent className="p-5 pt-0">
							<div className="h-8" aria-hidden />
							<div className="flex items-center justify-between gap-4">
								<PlanBadge />
								<Button variant="secondary" className="rounded-lg border-neutral-200 bg-neutral-50 hover:bg-neutral-100 focus-visible:ring-2 focus-visible:ring-neutral-300" onClick={() => setManageOpen(true)} disabled={hydrating || loading}>Manage</Button>
							</div>
						</CardContent>
					</Card>

					<Card className="md:col-span-2 rounded-xl border border-neutral-200 bg-white shadow-sm">
						<CardHeader className="pb-2 px-5">
							<div className="flex items-center justify-between text-sm">
								<div className="font-medium text-neutral-900">Credits remaining</div>
								<div className="text-neutral-600">{creditsLabel}</div>
							</div>
						</CardHeader>
						<CardContent className="p-5 pt-0">
								{!unlimited && <Progress value={barValue} className="h-2 bg-neutral-200 [&>div]:bg-[#ffd500]" />}
							<div className="mt-2 text-xs text-neutral-600">
                            {cancelAtPeriodEnd ? (
								<span>
                                    Cancellation scheduled. Plus plan remains until {billing?.subscription?.current_period_end ? new Intl.DateTimeFormat(undefined, { year: 'numeric', month: 'short', day: 'numeric' }).format(new Date(billing.subscription.current_period_end)) : (resetAt ? new Intl.DateTimeFormat(undefined, { year: 'numeric', month: 'short', day: 'numeric' }).format(resetAt) : '')}, then you'll move to Free.
								</span>
							) : (
								<>
                                    {resetAt && (
										<span>
											Credits reset on {new Intl.DateTimeFormat(undefined, { year: 'numeric', month: 'short', day: 'numeric' }).format(resetAt)}
										</span>
									)}
								</>
							)}
							</div>
						</CardContent>
					</Card>
				</div>

				{/* Manage section moved to popup; removed from main body */}

				{/* Plans grid */}
				<div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6 items-stretch mt-6">
					{/* Free */}
					<Card className="rounded-xl border border-neutral-200 bg-white shadow-sm h-full flex flex-col">
						<CardHeader className="p-5 pb-3 space-y-1.5">
							<div className="text-lg font-semibold">Free</div>
							<div className="h-8 flex items-center text-xs text-neutral-500 leading-snug truncate">No credit card required to get started</div>
						</CardHeader>
						<CardContent className="p-5 pt-0 space-y-3 flex-1">
							<div className="flex items-baseline gap-2"><div className="text-4xl font-semibold">$0</div><span className="text-base text-neutral-500">/month</span></div>
							<div className="h-8" aria-hidden />
							<Feature>300 credits per month (≈100 plots)</Feature>
							<Feature>1 project</Feature>
							<Feature>Unlimited GUI editing</Feature>
							<Feature>Export PNG, SVG, Python code</Feature>
						</CardContent>
						<CardFooter className="p-5 pt-0 mt-auto">
											{uiPlan === 'free' ? (
												<Button className="w-full border-neutral-200 bg-neutral-50 hover:bg-neutral-100 focus-visible:ring-2 focus-visible:ring-neutral-300" variant="secondary" disabled>
									Current plan
								</Button>
							) : (
												<Button className="w-full border-neutral-200 bg-neutral-50 hover:bg-neutral-100 focus-visible:ring-2 focus-visible:ring-neutral-300" variant="secondary" onClick={openPortal} disabled={hydrating || loading}>
									Change plan
								</Button>
							)}
						</CardFooter>
					</Card>

					{/* Plus */}
					<Card className="rounded-xl border border-neutral-200 bg-white shadow-sm h-full flex flex-col">
						<CardHeader className="p-5 pb-3 space-y-1.5 relative">
							<div className="flex items-start justify-between">
								<div>
									<div className="text-lg font-semibold">Plus</div>
									<div className="h-8 flex items-center text-xs text-neutral-500 leading-snug truncate">Built for professionals</div>
								</div>
							</div>
							<div className="absolute right-3 top-3">
								<span className="inline-block rounded-md bg-neutral-900/80 text-white text-[10px] font-semibold px-2 py-0.5 uppercase tracking-wide">Most Popular</span>
							</div>
						</CardHeader>
						<CardContent className="p-5 pt-0 space-y-3 flex-1">
							{/* Price row + savings pill (always visible if savings > 0) */}
							<div className="flex items-baseline justify-between gap-2">
								<div className="flex items-baseline gap-2">
									<div className="text-4xl font-semibold">{annual ? `$${priceYearly}` : `$${Number(priceMonthly).toFixed(2)}`}</div>
									<span className="text-base text-neutral-500">/{annual ? 'year' : 'month'}</span>
								</div>
								{savings > 0 && (
									<span className="inline-flex items-center rounded-full bg-[#fff2a3] text-neutral-900 text-xs px-2 py-0.5 font-medium">Save ${savings}/yr</span>
								)}
							</div>

							{/* Fixed-height meta row so buttons align across cards */}
							<div className="h-8 flex items-center justify-between gap-3 whitespace-nowrap">
								<div className="text-sm text-neutral-700">Annual <span className="text-neutral-500">(≈${(priceYearly / 12).toFixed(2)}/mo)</span></div>
								<Switch checked={annual} onCheckedChange={setAnnual} className="data-[state=checked]:bg-[#ffd500]" />
							</div>
							<Feature>Unlimited credits</Feature>
							<Feature>Unlimited projects</Feature>
							<Feature>2 concurrent generation jobs</Feature>
							<Feature>Priority support</Feature>
						</CardContent>
						<CardFooter className="p-5 pt-0 mt-auto">
								{uiPlan === 'plus' ? (
    isPlusCurrentSelection ? (
									<Button className="w-full rounded-lg border-neutral-200 bg-neutral-50 hover:bg-neutral-100 focus-visible:ring-2 focus-visible:ring-neutral-300" variant="secondary" disabled>
									Current plan
								</Button>
							) : (
										<Button className="w-full bg-primary text-primary-foreground hover:bg-primary/90 rounded-lg" onClick={upgrade} disabled={hydrating || loading}>
									Switch to {annual ? 'Yearly' : 'Monthly'}
								</Button>
							)
								) : (
									<Button className="w-full bg-primary text-primary-foreground hover:bg-primary/90 rounded-lg" onClick={upgrade} disabled={hydrating || loading}>Upgrade to Plus</Button>
								)}
						</CardFooter>
					</Card>

					{/* Enterprise */}
					<Card className="rounded-xl border border-neutral-200 bg-white shadow-sm h-full flex flex-col">
						<CardHeader className="p-5 pb-3 space-y-1.5">
							<div className="text-lg font-semibold">Team</div>
				<div className="h-8 flex items-center text-xs text-neutral-500 leading-snug truncate">Everything in Plus, for teams</div>
							<div className="text-3xl font-semibold">Custom<span className="text-base font-normal text-neutral-500">/org</span></div>
						</CardHeader>
						<CardContent className="p-5 pt-0 space-y-3 flex-1">
							<div className="h-8" aria-hidden />
							<Feature>Unlimited collaborators</Feature>
							<Feature>Team workspaces, shared datasets</Feature>
							<Feature>Role-based access</Feature>
							<Feature>Priority support, expedited queue</Feature>
						</CardContent>
						<CardFooter className="p-5 pt-0 mt-auto">
							<Button className="w-full border-neutral-200 bg-neutral-50 hover:bg-neutral-100 focus-visible:ring-2 focus-visible:ring-neutral-300" onClick={() => (window.location.href = 'mailto:hi@lumiplot.ai?subject=Lumiplot%20Team%20Plan')}>Contact sales</Button>
						</CardFooter>
					</Card>
				</div>

				{/* (removed helper text; managed above) */}
			</DialogContent>

			{/* Manage Plan popup */}
			<Dialog open={manageOpen} onOpenChange={setManageOpen}>
				<DialogContent className="fixed left-1/2 top-1/2 z-[10000] grid w-full max-w-lg -translate-x-1/2 -translate-y-1/2 gap-4 rounded-2xl border border-neutral-200 bg-white p-6 shadow-lg">
					<DialogHeader className="text-center sm:text-left">
						<DialogTitle className="text-[18px] font-semibold text-neutral-900">Manage Plan</DialogTitle>
						<DialogDescription className="text-sm text-neutral-600">Subscription & Billing Settings</DialogDescription>
					</DialogHeader>
					<hr className="border-neutral-200" />

					<div className="flex flex-col gap-4 py-4">
						<div className="flex items-center gap-4 rounded-xl bg-neutral-50 p-4">
							<div className="w-8 h-8 rounded-full bg-[#ffd500] text-neutral-900 flex items-center justify-center">
								<span className="text-xs font-semibold">{(user?.email?.charAt(0) || 'U').toUpperCase()}</span>
							</div>
							<div className="flex flex-col gap-0.5">
								<p className="text-base font-medium text-neutral-900">{plan === 'plus' ? "You're on Plus" : "You're on Starter Plan"}</p>
								{resetAt && (
									<p className="text-sm text-neutral-600">{plan === 'plus' ? (cancelAtPeriodEnd ? `Cancels ${new Intl.DateTimeFormat(undefined, { year: 'numeric', month: 'short', day: 'numeric' }).format(resetAt)}` : `Renews ${new Intl.DateTimeFormat(undefined, { year: 'numeric', month: 'short', day: 'numeric' }).format(resetAt)}`) : `Resets ${new Intl.DateTimeFormat(undefined, { year: 'numeric', month: 'short', day: 'numeric' }).format(resetAt)}`}</p>
								)}
							</div>
						</div>

						{plan === 'plus' && (
							<Button
								onClick={openPortal}
								className={`w-full rounded-lg h-10 ${cancelAtPeriodEnd ? "bg-[#ffd500] text-neutral-900 hover:bg-[#ffe34d] active:bg-[#ffd500]/90 ring-1 ring-inset ring-black/5 focus-visible:ring-2 focus-visible:ring-[#ffd500]" : "border border-neutral-200 bg-neutral-50 hover:bg-neutral-100"}`}
								variant={cancelAtPeriodEnd ? "default" : "outline"}
							>
							{'Manage plan'}
							</Button>
						)}
					</div>

					<hr className="border-neutral-200" />

					<div className="grid grid-cols-2 gap-2">
						<Button className="h-10 border-neutral-200 bg-neutral-50 hover:bg-neutral-100" variant="secondary" onClick={openPortal}>View Invoices</Button>
						<Button className="h-10 border-neutral-200 bg-neutral-50 hover:bg-neutral-100" onClick={openPortal}>Payment Preferences</Button>
					</div>
				</DialogContent>
			</Dialog>
		</Dialog>
	);
}
